import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Phone } from "lucide-react";
import { DESTINATIONS, COMPANY } from "@/data/siteData";

const PackageCard = ({ pkg, index, onEnquire }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className="group spotlight-card relative rounded-2xl overflow-hidden border border-white/10 bg-[#111A33]"
    data-testid={`package-card-${pkg.slug}`}
  >
    <div className="relative h-72 overflow-hidden">
      <img
        src={pkg.image}
        alt={pkg.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/20 to-transparent" />
      <span className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-[#0B132B]/75 backdrop-blur px-3 py-1.5 text-xs text-teal-300 border border-teal-400/20">
        <Clock className="w-3.5 h-3.5" /> {pkg.duration}
      </span>
    </div>
    <div className="p-6">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400/90">{pkg.tagline}</p>
      <h3 className="mt-2 font-serif text-xl sm:text-2xl">{pkg.name}</h3>
      <div className="mt-4 flex flex-wrap gap-2">
        {pkg.highlights.map((h) => (
          <span key={h} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-400">
            {h}
          </span>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <span className="text-sm text-slate-400 italic font-serif">Price on request</span>
        <button
          onClick={() => onEnquire(pkg.name)}
          data-testid={`package-contact-btn-${pkg.slug}`}
          className="rounded-full bg-teal-400/10 border border-teal-400/40 px-5 py-2.5 text-sm text-teal-300 transition-colors duration-300 hover:bg-teal-400 hover:text-[#0B132B]"
        >
          Contact for Price
        </button>
      </div>
    </div>
  </motion.article>
);

export const Destinations = ({ onEnquire }) => {
  const [tab, setTab] = useState("india");
  const list = DESTINATIONS[tab];

  return (
    <section id="packages" className="relative py-28 lg:py-36 bg-[#0E1730]" data-testid="destinations-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div className="max-w-xl">
            <p className="text-teal-300 tracking-[0.3em] text-xs uppercase mb-4">Signature packages</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">
              Destinations we <span className="italic text-teal-300">obsess</span> over.
            </h2>
            <p className="mt-4 text-sm text-slate-400">
              Every itinerary is quoted personally — no algorithms, no hidden fees. Call {COMPANY.phone} or send an enquiry.
            </p>
          </div>

          <div className="flex rounded-full border border-teal-500/25 p-1 bg-[#0B132B]/60" data-testid="destination-tabs">
            {[
              { id: "india", label: "India" },
              { id: "international", label: "International" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`rounded-full px-6 py-2.5 text-sm tracking-wide transition-colors duration-300 ${
                  tab === t.id ? "bg-teal-400 text-[#0B132B] font-semibold" : "text-slate-300 hover:text-teal-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {list.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} index={i} onEnquire={onEnquire} />
            ))}
          </AnimatePresence>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-12 text-center text-sm text-slate-500"
        >
          Looking for somewhere else? We craft packages across India & the world —{" "}
          <a href={COMPANY.phoneHref} data-testid="destinations-call-link" className="text-teal-300 hover:text-teal-200 underline underline-offset-4">
            just ask
          </a>
          .
        </motion.p>
      </div>
    </section>
  );
};

export default Destinations;
